import React, { useState, useEffect, useMemo, useRef, useCallback, ChangeEvent } from "react";
import "./style.css";
import { Back, Search } from "../../constants";
import { get, post } from "../../config/api";
import { API_ENDPOINTS } from "../../config/apiEndpoints";
import { Drawer } from "@mui/material";
import Webcam from "react-webcam";

interface FileWithPreview {
  file: File;
  preview: string;
}

interface ApproveModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  userData: {
    user_id: string | number;
  };
  idDistributor: boolean;
  requestData?: {
    request_id: string;
    partner_id: string;
    certificate?: {
      certificate_number: string;
      certificate_copy_first_page_url: string;
      certificate_copy_last_page_url: string;
    };
  };
}

const ApproveModal: React.FC<ApproveModalProps> = ({ show, onClose, onSubmit, userData, idDistributor, requestData }) => {
  // Step Management: 1 = Details, 2 = Delivery Code
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  
  // Form State
  const [certificateNo, setCertificateNo] = useState<string>("");
  const [expiryDate, setExpiryDate] = useState<string>(`${new Date().getFullYear()}-12-31`);
  const [noExpiry, setNoExpiry] = useState<boolean>(false);
  const [availableNumbers, setAvailableNumbers] = useState<string[]>([]);
  
  // Image State
  const [firstPageFile, setFirstPageFile] = useState<FileWithPreview | null>(null);
  const [lastPageFile, setLastPageFile] = useState<FileWithPreview | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<'first' | 'last' | null>(null);

  // Camera and Upload Modal State
  const [isUploadOptionOpen, setIsUploadOptionOpen] = useState(false);
  const [uploadTargetPage, setUploadTargetPage] = useState<'first' | 'last'>('first');
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Certificate Picker State
  const [showCertPicker, setShowCertPicker] = useState<boolean>(false);
  const [tempCertificateNo, setTempCertificateNo] = useState<string>("");
  const [certificateSearchTerm, setCertificateSearchTerm] = useState<string>("");

  // Delivery Code State
  const [codes, setCodes] = useState<string[]>(['', '', '', '']);
  const codeRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  useEffect(() => {
    if (show && requestData?.certificate?.certificate_number && 
        requestData?.certificate?.certificate_copy_first_page_url && 
        requestData?.certificate?.certificate_copy_last_page_url) {
      setCurrentStep(2);
    } else if (show) {
      setCurrentStep(1);
    }
  }, [show, requestData]);

  useEffect(() => {
    const fetchNumbers = async () => {
      try {
        const res = await get(API_ENDPOINTS.FETCH_AVAILABLE_NUMBERS(String(userData.user_id)));
        setAvailableNumbers(res || []);
      } catch (e) {
        console.error("Error fetching numbers:", e);
      }
    };
    if (show) fetchNumbers();
  }, [show, userData.user_id]);

  const confirmDelete = () => {
    if (deleteTarget === 'first') setFirstPageFile(null);
    if (deleteTarget === 'last') setLastPageFile(null);
    setDeleteTarget(null);
  };

  const handleUploadAreaClick = (page: 'first' | 'last') => {
    setUploadTargetPage(page);
    setIsUploadOptionOpen(true);
  };

  const handleUploadFromGallery = () => {
    setIsUploadOptionOpen(false);
    fileInputRef.current?.click();
  };

  const handleGalleryFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const previewUrl = URL.createObjectURL(file);
      const fileData: FileWithPreview = { file, preview: previewUrl };
      if (uploadTargetPage === 'first') setFirstPageFile(fileData);
      else setLastPageFile(fileData);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCameraOption = () => {
    setIsUploadOptionOpen(false);
    setIsCameraOpen(true);
  };

  const handleCloseCamera = () => {
    setIsCameraOpen(false);
    setCapturedImage(null);
  };

  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
    }
  }, [webcamRef]);

  const retakePhoto = () => {
    setCapturedImage(null);
  };

  const dataURLtoFile = (dataurl: string, filename: string) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const confirmCapture = () => {
    if (capturedImage) {
      const file = dataURLtoFile(capturedImage, `camera_capture_${Date.now()}.jpeg`);
      const previewUrl = URL.createObjectURL(file);
      const fileData: FileWithPreview = { file, preview: previewUrl };
      if (uploadTargetPage === 'first') setFirstPageFile(fileData);
      else setLastPageFile(fileData);
      handleCloseCamera();
    }
  };

  const handleCodeChange = (val: string, index: number) => {
    if (/^\d*$/.test(val) && val.length <= 1) {
      const newCodes = [...codes];
      newCodes[index] = val;
      setCodes(newCodes);
      
      if (val.length === 1 && index < 3) {
        codeRefs[index + 1].current?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      codeRefs[index - 1].current?.focus();
    }
  };

  const isStep1Valid = useMemo(() => {
    return certificateNo && (expiryDate || noExpiry) && firstPageFile && lastPageFile;
  }, [certificateNo, expiryDate, noExpiry, firstPageFile, lastPageFile]);

  const isStep2Valid = useMemo(() => codes.every(c => c !== ''), [codes]);

  const filteredAvailableNumbers = useMemo(() => {
    const term = certificateSearchTerm.trim().toLowerCase();
    if (!term) return availableNumbers;
    return availableNumbers.filter((num) => num.toLowerCase().includes(term));
  }, [availableNumbers, certificateSearchTerm]);

  useEffect(() => {
    if (showCertPicker) {
      setCertificateSearchTerm("");
    }
  }, [showCertPicker]);

  const uploadFile = async (file: File) => {
    try {
      const res = await get(
        `${API_ENDPOINTS.CERTIFICATES_PRESIGNED_URL}?fileName=${file.name}&fileType=${file.type}&partnerId=${requestData?.partner_id}&request_id=${requestData?.request_id}`
      );
      await fetch(res.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      return res.fileUrl;
    } catch (error) {
      console.error("File upload failed:", error);
      return null;
    }
  };

  const handleSaveNext = async () => {
    if (!isStep1Valid || !firstPageFile || !lastPageFile) return;

    setIsUploading(true);
    try {
      const [firstUrl, lastUrl] = await Promise.all([
        uploadFile(firstPageFile.file),
        uploadFile(lastPageFile.file),
      ]);

      if (!firstUrl || !lastUrl) {
        alert("Failed to upload certificate images. Please try again.");
        return;
      }

      const dataToSubmit = {
        request_id: Number(requestData?.request_id),
        // partner: {
        //   partner_id: requestData?.partner_id,
        // },
        certificate_number: certificateNo,
        issued_by: userData.user_id,
        expiry_date: noExpiry || !expiryDate ? null : expiryDate,
        certificate_copy_first_page_url: firstUrl,
        certificate_copy_last_page_url: lastUrl,
      };

      const response = await post(
        API_ENDPOINTS.ISSUE_CERTIFICATE,
        JSON.stringify(dataToSubmit)
      );

      if (response) {
        setCurrentStep(2);
      } else {
        alert("Failed to save certificate details. Please try again.");
      }
    } catch (error) {
      console.error("Error saving certificate details:", error);
      alert("An error occurred while saving. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFinalSubmit = () => {
    const deliveryCode = codes.join('');
    onSubmit({
      certificateNo,
      expiryDate: noExpiry || !expiryDate ? null : expiryDate,
      firstPage: firstPageFile?.file,
      lastPage: lastPageFile?.file,
      deliveryCode
    });
  };

  if (!show) return null;

  return (
    <div className="deliver-screen">
      {/* Header */}
      <div className="deliver-header">
        <img 
            src={Back} 
            className="back-icon" 
            onClick={currentStep === 2 && !(requestData?.certificate?.certificate_number) ? () => setCurrentStep(1) : onClose} 
            alt="back" 
        />
        <span className="header-title">Deliver Certificate</span>
      </div>

      {/* Stepper Progress */}
      <div className="stepper-wrapper">
        <div className="stepper-labels">
          <span className={`step-label ${currentStep === 1 ? 'active' : 'completed'}`}>
            {currentStep === 2 && <span className="check-mark">✓</span>} Enter Certificate Details
          </span>
          <span className={`step-label ${currentStep === 2 ? 'active' : 'inactive'}`}>
            Enter Delivery Code
          </span>
        </div>
        <div className="progress-bar-bg">
          <div className="progress-bar-fill" style={{ width: currentStep === 1 ? '35%' : '100%' }}></div>
        </div>
      </div>

      <div className="deliver-content">
        {currentStep === 1 ? (
          <>
            <div className="input-field">
              <label>Certificate no. <span className="req">*</span></label>
              <div 
                className="styled-select cert-picker-trigger" 
                onClick={() => { setTempCertificateNo(certificateNo); setShowCertPicker(true); }}
              >
                <span className={certificateNo ? '' : 'placeholder-text'}>
                  {certificateNo || 'Select from assigned range'}
                </span>
                <span className="dropdown-arrow">&#9662;</span>
              </div>
            </div>

            <div className="input-field">
              <label>Certificate Expiry Date <span className="req">*</span></label>
              <div className="date-input-container">
                <input 
                    type="date" 
                    value={expiryDate} 
                    onChange={(e) => setExpiryDate(e.target.value)} 
                    disabled={noExpiry} 
                />
              </div>
              <div className="checkbox-row">
                <input 
                    type="checkbox" 
                    id="noExp" 
                    checked={noExpiry} 
                    onChange={(e) => setNoExpiry(e.target.checked)} 
                />
                <label htmlFor="noExp">No Expiry Date</label>
              </div>
            </div>

            <label className="section-label">Certificate Images <span className="req">*</span></label>
            <div className="capture-row">
              {/* First Page */}
              <div className="capture-item">
                <span className="page-text">First Page</span>
                {firstPageFile ? (
                  <div className="preview-container">
                    <img src={firstPageFile.preview} className="img-preview" alt="first" />
                    <button className="delete-overlay-btn" onClick={() => setDeleteTarget('first')}>
                      <span className="trash-icon">🗑️</span>
                    </button>
                  </div>
                ) : (
                  <div className="upload-placeholder" onClick={() => handleUploadAreaClick('first')}>
                    <div className="cam-circle">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2196F3" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    </div>
                    <span className="tap-text">Tap to Capture</span>
                    <span className="sub-text">Open camera</span>
                  </div>
                )}
              </div>

              {/* Last Page */}
              <div className="capture-item">
                <span className="page-text">Last Page</span>
                {lastPageFile ? (
                  <div className="preview-container">
                    <img src={lastPageFile.preview} className="img-preview" alt="last" />
                    <button className="delete-overlay-btn" onClick={() => setDeleteTarget('last')}>
                       <span className="trash-icon">🗑️</span>
                    </button>
                  </div>
                ) : (
                  <div className="upload-placeholder" onClick={() => handleUploadAreaClick('last')}>
                    <div className="cam-circle">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2196F3" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    </div>
                    <span className="tap-text">Tap to Capture</span>
                    <span className="sub-text">Open camera</span>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="delivery-code-section">
            <label className="section-label">Enter 4 digit delivery code <span className="req">*</span></label>
            <div className="otp-container">
              {codes.map((c, i) => (
                <input
                  key={i}
                  ref={codeRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={c}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onChange={(e) => handleCodeChange(e.target.value, i)}
                  className="otp-box"
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="deliver-footer">
        {currentStep === 1 ? (
          <button 
            className={`footer-main-btn ${isStep1Valid ? 'active' : ''}`} 
            disabled={!isStep1Valid || isUploading} 
            onClick={handleSaveNext}
          >
            {isUploading ? 'Uploading...' : 'Save & Next'}
          </button>
        ) : (
          <button 
            className={`footer-main-btn ${isStep2Valid ? 'active' : ''}`} 
            disabled={!isStep2Valid}
            onClick={handleFinalSubmit}
          >
            Submit
          </button>
        )}
      </div>

      {/* Certificate Number Picker */}
      {showCertPicker && (
        <div className="cert-picker-overlay">
          <div className="cert-picker-sheet">
            <div className="cert-picker-header">
              <span className="cert-picker-title">Certificate no.</span>
              <span className="cert-picker-close" onClick={() => setShowCertPicker(false)}>&times;</span>
            </div>
            <div className="cert-picker-search-wrap">
              <input
                type="text"
                value={certificateSearchTerm}
                onChange={(e) => setCertificateSearchTerm(e.target.value)}
                className="cert-picker-search-input"
                style={{
                              backgroundImage: `url(${Search})`,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: '3px 16px',
                              backgroundSize: 21
                            }}
                placeholder="Search certificate number"
              />
            </div>
            <div className="cert-picker-list">
              {filteredAvailableNumbers.map(n => (
                <div 
                  key={n} 
                  className="cert-picker-item" 
                  onClick={() => setTempCertificateNo(n)}
                >
                  <span className="cert-picker-item-text">{n}</span>
                  <span className={`cert-picker-radio ${tempCertificateNo === n ? 'selected' : ''}`}>
                    {tempCertificateNo === n && <span className="cert-picker-radio-dot" />}
                  </span>
                </div>
              ))}
              {filteredAvailableNumbers.length === 0 && (
                <div className="cert-picker-empty">No matching certificate numbers</div>
              )}
            </div>
            <div className="cert-picker-footer">
              <button 
                className={`cert-picker-btn ${tempCertificateNo ? 'active' : ''}`} 
                disabled={!tempCertificateNo}
                onClick={() => { setCertificateNo(tempCertificateNo); setShowCertPicker(false); }}
              >
                Select
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for gallery upload */}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept="image/*"
        onChange={handleGalleryFileChange}
      />

      {/* Upload Option Drawer */}
      <Drawer
        anchor="bottom"
        open={isUploadOptionOpen}
        onClose={() => setIsUploadOptionOpen(false)}
        sx={{ zIndex: 3000 }}
      >
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="submit-button" style={{ backgroundColor: '#007cbf', width: '100%', padding: '12px', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' }} onClick={handleUploadFromGallery}>
            Upload from Gallery
          </button>
          <button className="submit-button" style={{ backgroundColor: '#007cbf', width: '100%', padding: '12px', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' }} onClick={handleCameraOption}>
            Open Camera
          </button>
        </div>
      </Drawer>

      {/* Camera View */}
      {isCameraOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999, backgroundColor: 'black', display: 'flex', flexDirection: 'column' }}>
          {!capturedImage ? (
            <>
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: 'environment' }}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
              <button
                onClick={capturePhoto}
                style={{ position: 'absolute', bottom: 30, left: '50%', transform: 'translateX(-50%)', width: 60, height: 60, borderRadius: '50%', backgroundColor: 'white', border: '4px solid #ccc' }}
              />
              <button
                onClick={handleCloseCamera}
                style={{ position: 'absolute', top: 20, right: 20, color: 'white', background: 'transparent', border: 'none', fontSize: 18 }}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <img src={capturedImage} alt="Captured" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              <div style={{ position: 'absolute', bottom: 30, left: 0, right: 0, display: 'flex', justifyContent: 'space-around' }}>
                <button onClick={retakePhoto} style={{ padding: '10px 20px', backgroundColor: 'white', border: 'none', borderRadius: 5 }}>Retake</button>
                <button onClick={confirmCapture} style={{ padding: '10px 20px', backgroundColor: '#007cbf', color: 'white', border: 'none', borderRadius: 5 }}>Use Photo</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <div className="dialog-overlay">
          <div className="dialog-box">
            <div className="dialog-header">
              <h3>Delete Photo?</h3>
              <span className="close-dialog" onClick={() => setDeleteTarget(null)}>&times;</span>
            </div>
            <p className="dialog-body">Are you sure you want to delete this photo?</p>
            <div className="dialog-actions">
              <button className="btn-delete-confirm" onClick={confirmDelete}>Yes, Delete</button>
              <button className="btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproveModal;